import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { EventLogTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PermissionGuard } from '~/core/PermissionGuard';
import { useCreateThreadWithCommentMutation } from '~/packages/comments/mutations/CreateThreadWithComment.gql.generated';
import { EventLogDrawer } from '~/packages/event-logs/EventLogDrawer';

// Constants
const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'limitedPartner_create',
  'limitedPartner_update',
  'limitedPartner_mapping_match',
  'limitedPartner_mapping_unmatch',
  'limitedPartner_thread_create',
];
const SINGLE_ENTITY_DRAWER_LABEL = 'Limited Partner Activity';
const MULTI_ENTITY_DRAWER_LABEL = 'Limited Partner Activity';

// Exports
type SingleEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
> & {
  entityId: string;
};
export const SingleEntityDrawer = (props: SingleEntityDrawerProps) => {
  const toast = useToast();
  const [createThread] = useCreateThreadWithCommentMutation();
  return (
    <PermissionGuard
      permissions={(ctx) =>
        ctx.flags.productsLimitedPartnersEventLogPermissions
      }
    >
      <EventLogDrawer
        {...props}
        entityThreadDomain="limitedPartner"
        entityIds={[props.entityId]}
        eventLogTypes={EVENT_LOG_TYPES}
        header={SINGLE_ENTITY_DRAWER_LABEL}
        triggerButtonProps={{ 'aria-label': SINGLE_ENTITY_DRAWER_LABEL }}
        onCreateThread={async ({ comment, participatingOrganizationIds }) => {
          try {
            const result = await createThread({
              variables: {
                comment,
                domain: 'limitedPartner',
                domainIdentifier: props.entityId,
                isPinned: false,
                participatingOrganizationIds,
              },
              refetchQueries: ['GetEventLogs'],
            });
            if (!result.data) throw Error('Failed to create thread');
          } catch (error) {
            Sentry.captureException(error);
            toast({
              title: 'Failed to create thread',
              status: 'error',
              position: 'top',
            });
          }
        }}
      />
    </PermissionGuard>
  );
};

type MultiEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
>;
export const MultiEntityDrawer = (props: MultiEntityDrawerProps) => {
  return (
    <PermissionGuard
      permissions={(ctx) =>
        ctx.flags.productsLimitedPartnersEventLogPermissions
      }
    >
      <EventLogDrawer
        {...props}
        entityIds={undefined}
        eventLogTypes={EVENT_LOG_TYPES}
        entityThreadDomain="limitedPartner"
        header={MULTI_ENTITY_DRAWER_LABEL}
        triggerButtonProps={{ 'aria-label': MULTI_ENTITY_DRAWER_LABEL }}
      />
    </PermissionGuard>
  );
};
